import {
  BLOCKS,
  Document as ContentfulDocument,
  TopLevelBlock,
} from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { ClientSide } from "~/@types";
import { IExpert } from "~/@types/generated/contentful";
import { getComponentFromContentfulRichTextEntry } from "~/contentful";
import { RoundedCta } from "../ctas";
import { Arrow } from "../icons/svgs";
import { ForcedAspectRatio, ContentfulCenteredImage } from "../shared";
import ExpertSocialLinks from "./ExpertSocialLinks";
import ExpertTypeBadge from "./ExpertTypeBadge";

export type ExpertHeroProps = {
  expert: ClientSide<IExpert>;
};

const isBlankParagraph = (node: TopLevelBlock) =>
  node.nodeType === BLOCKS.PARAGRAPH &&
  node.content.every(
    (child) => child.nodeType === "text" && child.value.trim() === "",
  );

export default function ExpertHero({ expert }: ExpertHeroProps) {
  const isCurrentTBI = expert.fields.type === "Current TBI";
  const bio = expert.fields.bio as ContentfulDocument;

  return (
    <section
      data-content-type="expertHero"
      data-module-title={expert.fields.slug}
      className="tbi-grid w-full pt-mobile-nav pb-16 lg:pt-48 lg:pb-20"
    >
      <div className="col-span-full mb-10 lg:col-span-6 lg:mb-0">
        {expert.fields.image && expert.fields.image.fields && isCurrentTBI ? (
          <ForcedAspectRatio ratio={[3, 4]} className="w-full">
            <ContentfulCenteredImage
              className="h-full w-full"
              image={expert.fields.image.fields}
              width={1400}
            />
          </ForcedAspectRatio>
        ) : (
          <ForcedAspectRatio
            ratio={[3, 4]}
            className="h1 flex-center align-center w-full bg-slate text-black"
          >
            {expert.fields.firstName?.[0]}
            {expert.fields.lastName?.[0]}
          </ForcedAspectRatio>
        )}
      </div>
      <div className="col-span-full self-center lg:col-span-5 lg:col-start-8">
        <div className="mb-6 flex flex-col gap-5">
          <h1 className="h4">
            {expert.fields.firstName} {expert.fields.lastName}
          </h1>
          {expert.fields.role && (
            <p className="eyebrows">{expert.fields.role}</p>
          )}
          <ExpertTypeBadge type={expert.fields.type} />
        </div>
        {expert.fields.showBio && isCurrentTBI && bio && (
          <div className="[&_p]:body mb-8 [&_p]:mt-6 [&_p]:leading-[120%] lg:[&_p]:mt-10">
            {documentToReactComponents(
              {
                ...bio,
                content: bio.content.filter((node) => !isBlankParagraph(node)),
              },
              getComponentFromContentfulRichTextEntry,
            )}
          </div>
        )}
        <div className="flex items-center justify-between lg:block">
          <ExpertSocialLinks
            expert={expert}
            className="justify-start [&_.cta]:hidden lg:[&_.cta]:inline [&_.flex-wrap]:gap-8 lg:[&_.flex-wrap]:gap-2"
          />
          {expert.fields.pressRequests && (
            <RoundedCta
              to={`mailto:${expert.fields.pressRequests}`}
              kind="black"
              icon={<Arrow />}
              label="Press Request"
              id={`expert-press-request-${expert.fields.slug}`}
              className="lg:mt-8"
            />
          )}
        </div>
      </div>
    </section>
  );
}
